import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Container,
  Typography,
  TextField,
  Button,
  Box,
  Snackbar,
  Alert,
  CircularProgress
} from '@mui/material';
import { useValidateConfirmation, useValidateConfirmationAndSetPassword } from 'src/api/user';
import { t } from 'i18next';
import { setSessionFromResponse } from 'src/auth/context/jwt/auth-provider-util';
import { useTranslation } from 'react-i18next';
import { paths } from 'src/routes/paths';
import { useRouter } from 'src/routes/hooks/use-router';

const ConfirmationPage: React.FC = () => {
  const location = useLocation();
  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [confirmationAttempted, setConfirmationAttempted] = useState(false);
  const { validateConfirmationAndSetPassword } = useValidateConfirmationAndSetPassword();
  const { validateConfirmation } = useValidateConfirmation();
  const router = useRouter();
  const { i18n } = useTranslation();

  const searchParams = new URLSearchParams(location.search);
  const confirmationToken = searchParams.get('confirmation_token');
  const isSignUp = searchParams.get('sign_up') === 'true';

  const handleConfirmation = useCallback(async () => {
    if (!confirmationToken || confirmationAttempted) return;

    setLoading(true);
    setConfirmationAttempted(true);
    try {
      const response = await validateConfirmation(confirmationToken);
      if (response.data.status === 'success') {
        setSessionFromResponse(response, i18n.changeLanguage, (user) => {
          window.location.href = paths.dashboard.applicants.root;
        });
      } else {
        setError(response.data.message || 'An error occurred');
      }
    } catch (err) {
      setError('An error occurred while confirming your account');
      console.error('Error confirming account:', err);
    } finally {
      setLoading(false);
    }
  }, [confirmationToken, validateConfirmation, i18n.changeLanguage, confirmationAttempted]);

  useEffect(() => {
    if (!isSignUp && confirmationToken && !confirmationAttempted) {
      handleConfirmation();
    }
  }, [isSignUp, confirmationToken, handleConfirmation, confirmationAttempted]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password !== passwordConfirmation) {
      setError("Passwords don't match");
      return;
    }
    if (!confirmationToken) return;

    setLoading(true);
    try {
      const response = await validateConfirmationAndSetPassword(confirmationToken, password, passwordConfirmation);
      if (response.data.status === 'success') {
        setSessionFromResponse(response, i18n.changeLanguage, (user) => {
          window.location.href = paths.dashboard.job_position.root;
        });
      } else {
        setError(response.data.message || 'An error occurred');
      }
    } catch (err) {
      setError('An error occurred while setting up your account');
      console.error('Error setting up account:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setError(null);
  };

  if (loading) {
    return (
      <Container maxWidth="sm">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (!isSignUp) {
    return (
      <Container maxWidth="sm">
        <Box sx={{ my: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            {error ? t('confirmation_failed') : t('confirming_account')}
          </Typography>
          {error && (
            <Typography color="error" sx={{ mt: 2 }}>
              {error}
            </Typography>
          )}
        </Box>
      </Container>
    );
  }

  return (
    <>
      <Helmet>
        <title>{t('set_password')}</title>
      </Helmet>
      <Container maxWidth="sm">
        <Box sx={{ my: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            {t('please_set_password')}
          </Typography>
          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              margin="normal"
              label={t('password.password_label')}
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
            <TextField
              fullWidth
              margin="normal"
              label={t('password.password_confirmation')}
              type="password"
              value={passwordConfirmation}
              onChange={(e) => setPasswordConfirmation(e.target.value)}
              required
            />
            <Button
              type="submit"
              variant="contained"
              color="primary"
              sx={{ mt: 3 }}
            >
              {t('set_password')}
            </Button>
          </form>
        </Box>
      </Container>
      <Snackbar open={!!error} autoHideDuration={6000} onClose={handleCloseSnackbar}>
        <Alert onClose={handleCloseSnackbar} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ConfirmationPage;
